// src/services/myPageService.js
//
// My Page API service
// ===================
//
// PURPOSE
// -------
// This file is the thin frontend API layer for the Dashboard / Inbox endpoint:
//
//   GET /my-page
//
// In your current application architecture this endpoint is the SINGLE
// authoritative source for dashboard content.
//
// CURRENT ARCHITECTURE RULES
// --------------------------
// 1) HTTP is authoritative
//    - Dashboard renders from GET /my-page only
//    - frontend must not recalculate inbox visibility logic on its own
//
// 2) Socket.IO is invalidation-only
//    - backend emits "myPageInvalidated"
//    - frontend reacts by refetching GET /my-page
//
// 3) Auth/session handling is NOT owned here
//    - apiClient attaches Device-Id / Browser-Id / X-Session-Id
//    - apiClient owns refresh-on-401 logic
//    - AuthContext / pages decide whether to softLogout
//
// Therefore this service should stay small and predictable.
//
// BACKEND CONTRACT
// ----------------
// Expected response:
//
//   {
//     counters: {
//       inbox_open: number,
//       overdue: number,
//       unread_chat: number,
//       new_inbox: number
//     },
//     inbox: [
//       {
//         key: string,
//         type: string,
//         title: string,
//         summary?: string,
//         commentsCount?: number,
//         hasUnreadChat?: boolean,
//         isNewInInbox?: boolean,
//         meta?: object
//       }
//     ]
//   }
//
// IMPORTANT DESIGN DECISION
// -------------------------
// We do normalize the payload defensively,
// but we do NOT invent new semantic names.
//
// That means:
// - backend snake_case counters remain snake_case
// - inbox item field names remain aligned with backend DTO
//
// Why this is the right choice:
// - easier debugging across frontend/backend
// - easier contract tracing in logs and dev tools
// - less "translation layer" confusion
//
// ERROR HANDLING POLICY
// ---------------------
// - request cancellation is normal control flow
// - non-cancellation failures are logged and rethrown
// - this file never performs redirects or logout actions

import apiClient from "../utils/apiClient";
import { isRequestCanceled } from "./httpUtils";

/* -------------------------------------------------------------------------- */
/* Small normalization helpers                                                */
/* -------------------------------------------------------------------------- */

/**
 * Convert a value into a finite number.
 *
 * Useful because backend data is expected to be numeric,
 * but defensive normalization makes the UI more resilient
 * during incremental backend/frontend development.
 *
 * @param {any} value
 * @param {number} fallback
 * @returns {number}
 */
function toNumber(value, fallback = 0) {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

/**
 * Convert a value into a boolean.
 *
 * This is intentionally tolerant:
 * - true / false stay unchanged
 * - 1 / 0 become booleans
 * - "true" / "false" are handled
 *
 * @param {any} value
 * @param {boolean} fallback
 * @returns {boolean}
 */
function toBoolean(value, fallback = false) {
  if (typeof value === "boolean") return value;
  if (value === 1 || value === "1") return true;
  if (value === 0 || value === "0") return false;
  if (typeof value === "string") {
    const v = value.trim().toLowerCase();
    if (v === "true") return true;
    if (v === "false") return false;
  }
  return fallback;
}

/**
 * Convert a value into a trimmed string.
 *
 * @param {any} value
 * @param {string} fallback
 * @returns {string}
 */
function toStringValue(value, fallback = "") {
  const s = typeof value === "string" ? value.trim() : String(value ?? "").trim();
  return s || fallback;
}

/**
 * Ensure a value is a plain object-like value suitable for "meta".
 *
 * @param {any} value
 * @returns {object}
 */
function toObject(value) {
  return value && typeof value === "object" && !Array.isArray(value) ? value : {};
}

/* -------------------------------------------------------------------------- */
/* Validation                                                                 */
/* -------------------------------------------------------------------------- */

/**
 * Validate the high-level shape of the /my-page payload.
 *
 * We intentionally keep validation LIGHT:
 * - backend is authoritative
 * - frontend/backend evolve together
 * - overly strict validation would create brittle UX during development
 *
 * We validate only the critical structure:
 * - response must be an object
 * - inbox must be an array
 * - counters, if present, must be an object
 *
 * @param {any} data
 */
function validateMyPagePayload(data) {
  if (!data || typeof data !== "object" || Array.isArray(data)) {
    throw new Error("Invalid /my-page response: expected an object.");
  }

  if (data.counters != null && (typeof data.counters !== "object" || Array.isArray(data.counters))) {
    throw new Error("Invalid /my-page response: counters must be an object.");
  }

  if (!Array.isArray(data.inbox)) {
    throw new Error("Invalid /my-page response: inbox must be an array.");
  }
}

/* -------------------------------------------------------------------------- */
/* Payload normalization                                                      */
/* -------------------------------------------------------------------------- */

/**
 * Normalize one inbox item into a stable frontend shape.
 *
 * IMPORTANT:
 * - this is NOT a semantic remapping layer
 * - it is only a defensive stabilization layer
 *
 * We keep backend field names, but ensure types are predictable.
 *
 * @param {any} item
 * @returns {object}
 */
function normalizeInboxItem(item) {
  const src = toObject(item);

  return {
    key: toStringValue(src.key),
    type: toStringValue(src.type),
    title: toStringValue(src.title),
    summary: toStringValue(src.summary),

    commentsCount: toNumber(src.commentsCount, 0),
    hasUnreadChat: toBoolean(src.hasUnreadChat, false),
    isNewInInbox: toBoolean(src.isNewInInbox, false),

    meta: toObject(src.meta),

    // Keep optional fields if backend sends them.
    ...(src.threadId != null ? { threadId: src.threadId } : {}),
  };
}

/**
 * Normalize the full /my-page payload.
 *
 * Guarantees:
 * - all counters exist and are numbers
 * - inbox is always an array
 * - each inbox item has a stable shape
 *
 * @param {any} data
 * @returns {{counters: object, inbox: object[]}}
 */
function normalizeMyPagePayload(data) {
  const counters = toObject(data?.counters);
  const inbox = Array.isArray(data?.inbox) ? data.inbox : [];

  return {
    counters: {
      inbox_open: toNumber(counters.inbox_open, 0),
      overdue: toNumber(counters.overdue, 0),
      unread_chat: toNumber(counters.unread_chat, 0),
      new_inbox: toNumber(counters.new_inbox, 0),
    },
    inbox: inbox.map(normalizeInboxItem),
  };
}

/* -------------------------------------------------------------------------- */
/* Public API                                                                 */
/* -------------------------------------------------------------------------- */

/**
 * Fetch the authoritative Dashboard / Inbox payload.
 *
 * Endpoint:
 *   GET /my-page
 *
 * Notes:
 * - apiClient handles identity headers and refresh behavior
 * - this service only fetches + validates + normalizes
 * - caller decides what to do with 401 or other failures
 *
 * Typical caller:
 * - Dashboard initial load
 * - Dashboard refresh after socket invalidation
 * - any future "My Page" widgets or panels
 *
 * @param {object} [options]
 * @param {AbortSignal} [options.signal]
 * @returns {Promise<{counters: object, inbox: object[]}>}
 */
export async function fetchMyPage(options = {}) {
  try {
    const response = await apiClient.get("/my-page", {
      withCredentials: true,
      signal: options.signal,
    });

    const data = response?.data;

    validateMyPagePayload(data);

    return normalizeMyPagePayload(data);
  } catch (error) {
    /**
     * Abort/cancel is normal control flow:
     * - route changed
     * - request was superseded
     * - component unmounted
     */
    if (isRequestCanceled(error)) {
      throw error;
    }

    console.error("[MyPageService] ❌ fetchMyPage failed:", error);
    throw error;
  }
}

/* -------------------------------------------------------------------------- */
/* Default export                                                             */
/* -------------------------------------------------------------------------- */

export default {
  fetchMyPage,
};